import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RouteAnalysisResult } from '@types';
import { ICONS } from '../constants';
import { GoogleMap, useGoogleMap, Marker, InfoWindow, Circle } from '@react-google-maps/api';
import { formatEventRiskScore } from '../../utils/riskEventFeed';
import { formatEventConfidence } from '../../utils/eventConfidence';
import { createSafeMarkdown } from '../../utils/safeMarkdown';
import { clearRoutePolylines, computeDrivingRoutes, getRouteMetrics, renderRoutePolylines } from '../../utils/googleRoutes';
import useGoogleMaps from '../useGoogleMaps';

const md = createSafeMarkdown();

interface RouteAnalysisDisplayProps {
    report: RouteAnalysisResult;
    onSave: () => void;
    isSaving: boolean;
    onCustomSave?: () => void;
    customSaveLabel?: string;
    onDownloadPDF: () => void;
}

const mapContainerStyle = {
    width: '100%',
    height: '500px'
};

const defaultCenter = {
    lat: 38.9072,
    lng: -77.0369
};

const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

type RouteType = 'planned' | 'secondary' | 'detour' | 'unplanned';

interface ModernRouteLayersProps {
    routes: Record<RouteType, any>;
    origin: string | google.maps.LatLngLiteral;
    destination: string | google.maps.LatLngLiteral;
    activeRoute: RouteType;
    onMetrics: (type: RouteType, metrics: { distance: string; duration: string }) => void;
}

const getRouteColor = (type: string) => {
    switch (type) {
        case 'planned': return '#10b981';
        case 'secondary':
        case 'secondary_strategic': return '#2563eb';
        case 'detour': return '#f59e0b';
        case 'unplanned': return '#64748b';
        default: return '#3b82f6';
    }
};

const ModernRouteLayers: React.FC<ModernRouteLayersProps> = ({
    routes,
    origin,
    destination,
    activeRoute,
    onMetrics
}) => {
    const map = useGoogleMap();
    const [computedRoutes, setComputedRoutes] = useState<Partial<Record<RouteType, google.maps.routes.Route>>>({});
    const polylinesRef = useRef<google.maps.Polyline[]>([]);
    const dragHandleRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const dragListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const dragPointsRef = useRef<Partial<Record<RouteType, google.maps.LatLngLiteral>>>({});
    const requestTokensRef = useRef<Record<RouteType, number>>({ planned: 0, secondary: 0, detour: 0, unplanned: 0 });

    const computeRoute = useCallback(async (type: RouteType, dragPoint?: google.maps.LatLngLiteral) => {
        const routeData = routes[type];
        if (!routeData)
            return;

        const requestToken = ++requestTokensRef.current[type];
        const intermediates: google.maps.routes.Waypoint[] = (routeData.waypoints || []).map((waypoint: string) => ({
            location: waypoint,
            via: true
        }));
        if (dragPoint)
            intermediates.splice(Math.ceil(intermediates.length / 2), 0, { location: dragPoint, via: true });

        try {
            const candidates = await computeDrivingRoutes({
                origin,
                destination,
                intermediates,
                computeAlternativeRoutes: type === 'secondary'
            });
            if (requestTokensRef.current[type] !== requestToken || !candidates.length)
                return;

            const route = candidates[type === 'secondary' ? 1 : 0] || candidates[0];
            setComputedRoutes(current => ({ ...current, [type]: route }));
            onMetrics(type, getRouteMetrics(route));
        } catch (error) {
            if (requestTokensRef.current[type] === requestToken)
                console.warn(`Route request failed for ${type}:`, error);
        }
    }, [destination, onMetrics, origin, routes]);

    useEffect(() => {
        dragPointsRef.current = {};
        setComputedRoutes({});
        const routeTypes = Object.keys(routes) as RouteType[];
        for (let index = 0; index < routeTypes.length; index++)
            if (routes[routeTypes[index]])
                void computeRoute(routeTypes[index]);

        return () => {
            for (let index = 0; index < routeTypes.length; index++)
                requestTokensRef.current[routeTypes[index]]++;
        };
    }, [computeRoute, routes]);

    useEffect(() => {
        clearRoutePolylines(polylinesRef.current);
        polylinesRef.current = [];
        if (!map)
            return;

        const entries = Object.entries(computedRoutes) as [RouteType, google.maps.routes.Route][];
        for (let index = 0; index < entries.length; index++) {
            const [type, route] = entries[index];
            const active = type === activeRoute;
            polylinesRef.current.push(...renderRoutePolylines(route, map, {
                strokeColor: getRouteColor(type),
                strokeWeight: active ? 6 : 3,
                strokeOpacity: active ? 0.9 : 0.3,
                zIndex: active ? 100 : 1
            }));
        }

        return () => {
            clearRoutePolylines(polylinesRef.current);
            polylinesRef.current = [];
        };
    }, [activeRoute, computedRoutes, map]);

    useEffect(() => {
        dragListenerRef.current?.remove();
        dragListenerRef.current = null;
        if (dragHandleRef.current)
            dragHandleRef.current.map = null;
        dragHandleRef.current = null;

        const route = computedRoutes[activeRoute];
        const path = route?.path;
        if (!map || !path?.length)
            return;

        let cancelled = false;
        void google.maps.importLibrary('marker').then(library => {
            if (cancelled)
                return;
            const { AdvancedMarkerElement } = library as google.maps.MarkerLibrary;
            const content = document.createElement('div');
            content.className = 'h-5 w-5 rounded-full border-4 border-white shadow-lg cursor-grab';
            content.style.backgroundColor = getRouteColor(activeRoute);
            content.title = 'Drag to adjust route';
            const marker = new AdvancedMarkerElement({
                map,
                position: dragPointsRef.current[activeRoute] || path[Math.floor(path.length / 2)],
                content,
                title: 'Drag to adjust route',
                gmpDraggable: true,
                zIndex: 200
            });
            dragHandleRef.current = marker;
            dragListenerRef.current = marker.addListener('dragend', () => {
                const position: any = marker.position;
                if (!position)
                    return;
                const point = {
                    lat: typeof position.lat === 'function' ? position.lat() : Number(position.lat),
                    lng: typeof position.lng === 'function' ? position.lng() : Number(position.lng)
                };
                if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng))
                    return;
                dragPointsRef.current[activeRoute] = point;
                void computeRoute(activeRoute, point);
            });
        });

        return () => {
            cancelled = true;
            dragListenerRef.current?.remove();
            dragListenerRef.current = null;
            if (dragHandleRef.current)
                dragHandleRef.current.map = null;
            dragHandleRef.current = null;
        };
    }, [activeRoute, computeRoute, computedRoutes, map]);

    return null;
};

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; className?: string }> = ({ title, icon, children, className }) => (
    <div className={`report-print-section bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-6 ${className}`}>
        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
            <span className="text-blue-600 bg-blue-50 p-2 rounded-lg">{icon}</span>
            {title}
        </h2>
        {children}
    </div>
);

const RouteAnalysisDisplay: React.FC<RouteAnalysisDisplayProps> = ({
    report,
    onSave,
    isSaving,
    onCustomSave,
    customSaveLabel,
    onDownloadPDF
}) => {
    const { isLoaded } = useGoogleMaps();

    const [activeRoute, setActiveRoute] = useState<RouteType>('planned');
    const [routeDistances, setRouteDistances] = useState<{ [key: string]: { distance: string, duration: string } }>({});
    const [selectedMarker, setSelectedMarker] = useState<RouteAnalysisResult['markers'][0] | null>(null);

    const routes = useMemo<Record<RouteType, any>>(() => ({
        planned: report.routes?.planned || (report.routes as any)?.primary,
        secondary: report.routes?.secondary_strategic || report.routes?.secondary,
        detour: report.routes?.detour,
        unplanned: report.routes?.unplanned
    }), [report.routes]);
    const primaryRoute = routes.planned;
    const secondaryRoute = routes.secondary;

    const handleRouteMetrics = useCallback((type: RouteType, metrics: { distance: string; duration: string }) => {
        setRouteDistances(current => ({ ...current, [type]: metrics }));
    }, []);

    const getMarkerIcon = (type: string) => {
        switch (type.toLowerCase()) {
            case 'origin': return 'http://maps.google.com/mapfiles/ms/icons/green-dot.png';
            case 'destination': return 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
            case 'threat zone':
            case 'threat': return 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png';
            case 'safe haven': return 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png';
            case 'hospital': return 'http://maps.google.com/mapfiles/ms/icons/hospitals.png';
            case 'police': return 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
            case 'us embassy':
            case 'embassy': return 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png';
            default: return 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div className="flex flex-col">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-500/20 text-blue-400 p-2 rounded-lg">
                            {ICONS.map}
                        </span>
                        <h1 className="text-xl font-bold text-white tracking-widest uppercase">EXECUTIVE PROTECTION MOVEMENT PLAN</h1>
                    </div>
                    <p className="text-slate-400 text-sm font-mono mt-1">
                        ROUTE: {report.routeName} | TRAVEL DATES: {report.travelDates?.start} TO {report.travelDates?.end}
                    </p>
                </div>
                <div className="flex gap-3 w-full md:w-auto">
                    <button 
                        onClick={onDownloadPDF}
                        disabled={isSaving}
                        className={`flex-1 md:flex-none hover:bg-slate-700 bg-slate-600 disabled:bg-slate-800 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-all flex items-center justify-center uppercase tracking-widest text-xs`}
                    >
                        Print / Save PDF
                    </button>
                    <button 
                        onClick={onSave}
                        disabled={isSaving}
                        className={`flex-1 md:flex-none ${onCustomSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-500'} disabled:bg-slate-600 text-white font-bold py-2 px-6 rounded-lg shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all flex items-center justify-center uppercase tracking-widest text-xs`}
                    >
                        {isSaving ? 'Processing...' : (customSaveLabel || "Sign & Archive Plan")}
                    </button>
                </div>
            </div>

            {/* Executive Summary & Threat Assessment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                    <h3 className="font-bold text-slate-800 uppercase text-sm mb-2 border-b border-blue-200 pb-1 flex items-center gap-2">
                        {ICONS.activity} Executive Summary
                    </h3>
                    <p className="text-sm text-slate-700">{report.executiveSummary}</p>
                </div>
                <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                    <h3 className="font-bold text-red-800 uppercase text-sm mb-2 border-b border-red-200 pb-1 flex items-center gap-2">
                        {ICONS.alert_triangle} Threat Assessment
                    </h3>
                    <p className="text-sm text-red-700">{report.threatAssessment}</p>
                </div>
            </div>

            {isLoaded ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8 relative">
                    <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            {ICONS.map} Strategic Planning Canvas
                        </h3>
                        <div className="flex gap-4 text-[10px] font-bold uppercase tracking-wider">
                            {(Object.keys(routes) as Array<keyof typeof routes>).map(rType => (
                                routes[rType] && (
                                    <button 
                                        key={rType}
                                        onClick={() => setActiveRoute(rType)}
                                        className={`flex items-center gap-1.5 transition-all ${activeRoute === rType ? 'scale-110' : 'opacity-40 hover:opacity-100'}`}
                                    >
                                        <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: getRouteColor(rType), border: rType === 'unplanned' ? '1px dashed white' : 'none' }}></span>
                                        {rType}
                                    </button>
                                )
                            ))}
                        </div>
                    </div>

                    <div className="absolute top-20 right-4 z-10 bg-white/90 backdrop-blur p-3 rounded-lg border border-slate-200 shadow-xl text-[10px] space-y-2 pointer-events-none">
                        <div className="font-black border-b border-slate-200 pb-1 mb-1 tracking-widest uppercase">Tactical Legend</div>
                        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500 opacity-20 border border-red-500"></span> Danger Zone</div>
                        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-purple-500"></span> Safe Haven</div>
                        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-500"></span> Public Service</div>
                        <div className="flex items-center gap-2"><span className="w-3 h-1 bg-emerald-500"></span> Planned Path</div>
                    </div>

                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={report.mapData?.center || defaultCenter}
                        zoom={12}
                        options={{
                            mapId: MAP_ID,
                            gestureHandling: 'cooperative',
                            styles: [
                                { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#7c9bad" }] },
                                { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#f8f9fa" }] }
                            ]
                        }}
                    >
                        {report.mapData?.origin && report.mapData?.destination && (
                            <ModernRouteLayers
                                routes={routes}
                                origin={report.mapData.origin}
                                destination={report.mapData.destination}
                                activeRoute={activeRoute}
                                onMetrics={handleRouteMetrics}
                            />
                        )}

                        {/* Custom Danger Zone Circles */}
                        {report.markers?.map((marker, index) => {
                            if (marker.type.toLowerCase() === 'threat zone') {
                                return (
                                    <React.Fragment key={`circle-${index}`}>
                                        <Circle
                                            center={{ lat: marker.lat, lng: marker.lng }}
                                            radius={marker.radius || (marker.riskScore ? marker.riskScore * 10 : 500)}
                                            options={{
                                                fillColor: '#ef4444',
                                                fillOpacity: 0.2,
                                                strokeColor: '#ef4444',
                                                strokeWeight: 1,
                                                clickable: false,
                                                editable: false,
                                                visible: true,
                                                zIndex: 1
                                            }}
                                        />
                                        <Marker
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            icon={getMarkerIcon(marker.type)}
                                            onClick={() => setSelectedMarker(marker)}
                                        />
                                    </React.Fragment>
                                );
                            }
                            return (
                                <Marker
                                    key={index}
                                    position={{ lat: marker.lat, lng: marker.lng }}
                                    title={marker.name}
                                    icon={getMarkerIcon(marker.type)}
                                    onClick={() => setSelectedMarker(marker)}
                                />
                            );
                        })}

                        {selectedMarker && (
                            <InfoWindow
                                position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                                onCloseClick={() => setSelectedMarker(null)}
                            >
                                <div className="p-3 max-w-xs">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`w-2 h-2 rounded-full ${selectedMarker.type === 'Threat Zone' ? 'bg-red-500 animate-pulse' : 'bg-blue-500'}`}></span>
                                        <h4 className="font-bold text-sm">{selectedMarker.name}</h4>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        <span className="text-[9px] font-black uppercase bg-slate-100 px-2 py-0.5 rounded text-slate-600 border border-slate-200">{selectedMarker.type}</span>
                                        {selectedMarker.likelihood && <span className="text-[9px] font-black uppercase bg-red-50 px-2 py-0.5 rounded text-red-600 border border-red-100">P: {selectedMarker.likelihood}</span>}
                                        {selectedMarker.impact && <span className="text-[9px] font-black uppercase bg-orange-50 px-2 py-0.5 rounded text-orange-600 border border-orange-100">I: {selectedMarker.impact}</span>}
                                    </div>
                                    {selectedMarker.contactInfo && <p className="text-xs text-slate-600 border-t pt-2 italic">{selectedMarker.contactInfo}</p>}
                                    {(selectedMarker.type === 'Threat Zone' || selectedMarker.riskScore != null) && (
                                        <div className="text-xs text-slate-700 border-t pt-2 space-y-1">
                                            <p>Risk: {formatEventRiskScore(selectedMarker)}</p>
                                            <p>Confidence: {formatEventConfidence(selectedMarker)}</p>
                                        </div>
                                    )}
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </div>
            ) : (
                <div className="h-[500px] bg-slate-100 flex items-center justify-center rounded-xl border border-slate-200">
                    <p className="text-slate-500 font-medium">Initializing Tactical Telemetry...</p>
                </div>
            )}

            {/* NEW: Strategic Comparative Dashboard */}
            <Section title="Comparative Risk & Logistics Analysis" icon={ICONS.activity}>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-slate-200 text-[10px] uppercase text-slate-400 font-black tracking-widest">
                                <th className="pb-3 pl-2 text-slate-800">Route Type</th>
                                <th className="pb-3">Distance</th>
                                <th className="pb-3">Est. Time</th>
                                <th className="pb-3">Risk Level</th>
                                <th className="pb-3">Support Proximity</th>
                                <th className="pb-3 text-right pr-2">Action</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {(Object.keys(routes) as Array<keyof typeof routes>).map(rType => (
                                routes[rType] && (
                                    <tr key={rType} className={`border-b border-slate-50 transition-all ${activeRoute === rType ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}>
                                        <td className="py-4 pl-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-1 h-8 rounded-full" style={{ backgroundColor: getRouteColor(rType) }}></div>
                                                <div>
                                                    <div className="font-bold text-slate-800 uppercase text-xs">{rType.replace('_', ' ')}</div>
                                                    <div className="text-[10px] text-slate-500 font-mono truncate max-w-[200px]">{routes[rType]?.description}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="font-mono text-xs font-bold text-slate-600">{routeDistances[rType]?.distance || '---'}</td>
                                        <td className="font-mono text-xs font-bold text-slate-600">{routeDistances[rType]?.duration || '---'}</td>
                                        <td>
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${rType === 'planned' ? 'bg-emerald-100 text-emerald-700' : rType === 'detour' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                                {rType === 'planned' ? 'Standard' : rType === 'detour' ? 'Optimized' : 'Elevated'}
                                            </span>
                                        </td>
                                        <td className="text-xs text-slate-500">
                                            {rType === 'planned' ? '< 4km' : '8.2km'}
                                        </td>
                                        <td className="text-right pr-2">
                                            <button 
                                                onClick={() => setActiveRoute(rType)}
                                                className={`text-[10px] font-black uppercase px-3 py-1 rounded transition-all ${activeRoute === rType ? 'bg-slate-800 text-white' : 'border border-slate-200 text-slate-500 hover:bg-slate-100'}`}
                                            >
                                                {activeRoute === rType ? 'Active' : 'Select'}
                                            </button>
                                        </td>
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Section title="Route Assessment" icon={ICONS.activity}>
                    <div className="space-y-4">
                        {primaryRoute && (
                            <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-slate-800 capitalize flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-blue-500 inline-block"></span>
                                        Primary Route
                                    </h3>
                                    <span className="text-xs font-semibold bg-white px-2 py-1 rounded shadow-sm border border-slate-100">
                                        est. {primaryRoute.estimatedMinutes} min
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700">{primaryRoute.description}</p>
                            </div>
                        )}
                        {secondaryRoute && (
                            <div className="p-4 rounded-lg border bg-slate-50 border-slate-200">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-slate-800 capitalize flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-slate-400 inline-block"></span>
                                        Secondary Route
                                    </h3>
                                    <span className="text-xs font-semibold bg-white px-2 py-1 rounded shadow-sm border border-slate-100">
                                        est. {secondaryRoute.estimatedMinutes} min
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700">{secondaryRoute.description}</p>
                            </div>
                        )}
                    </div>
                </Section>

                <Section title="EP Team & Logistics" icon={ICONS.users}>
                    {report.epTeam ? (
                        <div className="space-y-6">
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 mb-2 border-b pb-1">Detail Composition</h4>
                                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{report.epTeam.composition}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 mb-2 border-b pb-1">Vehicle Requirements</h4>
                                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{report.epTeam.vehicles}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-slate-700 mb-2 border-b pb-1">Equipment</h4>
                                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded font-mono text-xs">{report.epTeam.equipment}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 italic">No EP team logistics provided.</p>
                    )}
                </Section>
            </div>

            {report.travelSchedule && report.travelSchedule.length > 0 && (
                <Section title="Movement Schedule" icon={ICONS.clock}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-3 text-xs font-bold text-slate-500 uppercase">Time</th>
                                    <th className="p-3 text-xs font-bold text-slate-500 uppercase">Location</th>
                                    <th className="p-3 text-xs font-bold text-slate-500 uppercase">Activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.travelSchedule.map((item, i) => (
                                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                        <td className="p-3 text-sm font-mono font-semibold text-slate-700 w-40 whitespace-nowrap">
                                            {item.startTime} - {item.endTime}
                                        </td>
                                        <td className="p-3 text-sm text-slate-800 font-medium">{item.location}</td>
                                        <td className="p-3 text-sm text-slate-600">{item.activity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}

            {report.emergencyActionPlan && report.emergencyActionPlan.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm mb-6">
                    <h2 className="text-xl font-bold text-red-800 mb-6 flex items-center gap-3 border-b border-red-200 pb-3">
                        <span className="bg-red-100 text-red-600 p-2 rounded-lg">{ICONS.alert_triangle}</span>
                        IMMEDIATE ACTION PROTOCOLS (IAP)
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {report.emergencyActionPlan.map((plan, i) => (
                            <div key={i} className="bg-white p-4 rounded-lg border border-red-100 shadow-sm">
                                <h4 className="font-bold text-slate-800 mb-2 uppercase text-sm border-b pb-1">{plan.incidentType}</h4>
                                <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                                    {plan.immediateActions.map((action, j) => <li key={j}>{action}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {report.additionalSections && report.additionalSections.length > 0 && (
                <div className="space-y-6">
                    {report.additionalSections.map((section: any, index: number) => (
                        <Section key={index} title={section.title || section.heading || 'Tactical Directive'} icon={ICONS.activity}>
                            <div className="prose prose-slate max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} />
                        </Section>
                    ))}
                </div>
            )}
            
            <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 italic">
                This movement plan is Insights generated for planning purposes. All routes, abort points, and safe havens must be physically advanced (advon) by the EP team prior to principal movement.
            </div>
        </div>
    );
};

export default RouteAnalysisDisplay;
